<template>
    <div class="drawer-backdrop" @click.self="closeDrawer">
        <transition name="slide-in-right">
            <div class="block-drawer">
                <!-- Header -->
                <div class="drawer-header">
                    <div class="header-content">
                        <h2 class="block-name">{{ data?.licenseName }} block</h2>
                        <p class="block-id">{{ data?.blockId }}</p>
                        <!-- The table prints "Disabled" for a block with nothing renewing,
                             so the drill-down has to agree: promising a date here that the
                             row which opened it calls off is worse than either alone. Same
                             predicate as that column, autoRenewOn > 0. -->
                        <p class="block-meta">
                            <template v-if="renewalIsOff">Renewal disabled</template>
                            <template v-else>Renews {{ formatBillingDate(data?.nextBillDate) || 'N/A' }}</template>
                            · {{ formatCurrency(data?.renewalPriceCents) }}/yr
                        </p>
                    </div>
                    <button type="button" class="close-btn" @click="closeDrawer">
                        <PhX :size="20" />
                    </button>
                </div>

                <!-- Bulk action -->
                <div class="drawer-bulk">
                    <div class="drawer-bulk__copy">
                        <span class="drawer-bulk__title">Renew all licenses in this block</span>
                        <span class="drawer-bulk__hint">
                            <template v-if="bulkStateKnown">
                                Flips every license's auto-renew at once.
                            </template>
                            <template v-else>
                                Clear the search and scroll to load every license to renew the whole block at once.
                            </template>
                        </span>
                    </div>
                    <PrimeToggleSwitch
                        :modelValue="bulkRenewing"
                        :disabled="bulkBusy || togglingIds.size > 0 || meta.total === 0 || !bulkStateKnown"
                        @update:modelValue="(value: boolean) => handleBulkToggle(value)"
                    />
                </div>

                <!-- Body -->
                <div v-if="!initialLoaded" class="drawer-loading">
                    <PrimeSkeleton v-for="n in 4" :key="n" width="100%" height="56px" class="mb-3" />
                </div>

                <div v-else-if="unfilteredTotal === 0" class="drawer-empty">
                    <p>No active licenses in this block.</p>
                </div>

                <div v-else ref="scrollRoot" class="drawer-body">
                    <section class="drawer-section">
                        <div class="section-head">
                            <h3 class="section-title">LICENSES</h3>
                            <span class="section-count">{{ meta.total }} {{ meta.total === 1 ? 'license' : 'licenses' }}</span>
                        </div>

                        <div class="drawer-search">
                            <PhMagnifyingGlass :size="16" class="drawer-search__icon" />
                            <input
                                v-model="searchQuery"
                                type="search"
                                class="drawer-search__input"
                                placeholder="Search by name or email"
                                aria-label="Search licenses by employee name or email"
                            />
                        </div>

                        <ul v-if="seats.length" class="seat-list" :class="{ 'seat-list--loading': isLoading }">
                            <li
                                v-for="seat in seats"
                                :key="seat.id"
                                class="seat-row"
                                :class="{ 'seat-row--unassigned': !seat.person_id }"
                            >
                                <div class="seat-meta">
                                    <span class="seat-person">
                                        {{ seat.person_name && seat.person_name.trim()
                                            ? seat.person_name
                                            : 'Unassigned' }}
                                    </span>
                                    <span class="seat-detail">{{ seat.person_email || '—' }}</span>
                                </div>
                                <div class="seat-toggle">
                                    <span
                                        class="seat-toggle-label"
                                        :class="{ 'seat-toggle-label--off': !seat.auto_renew }"
                                    >
                                        {{ seat.auto_renew ? 'Renewing' : 'Not renewing' }}
                                    </span>
                                    <PrimeToggleSwitch
                                        :modelValue="seat.auto_renew"
                                        :disabled="seat.expired || togglingIds.has(seat.id) || bulkBusy"
                                        @update:modelValue="(value: boolean) => handleToggle(seat, value)"
                                    />
                                </div>
                            </li>
                        </ul>

                        <p v-else class="drawer-no-matches">
                            No licenses match "{{ searchQuery.trim() }}".
                        </p>

                        <div v-if="hasMore" ref="sentinel" class="drawer-sentinel">
                            <PrimeSkeleton v-if="loadingMore" width="100%" height="56px" />
                        </div>

                        <p class="drawer-help">
                            Turning a license off lets it lapse on the next bill date — it won't be invoiced again
                            and the assigned employee loses coverage at that time.
                        </p>
                    </section>
                </div>
            </div>
        </transition>
    </div>
</template>

<script setup lang="ts">
import { PhX, PhMagnifyingGlass } from '@phosphor-icons/vue';
import { formatCurrency } from '@privacy-bee/format/currency';
import { businessService } from '~/services/business-service';
import { formatBillingDate } from '~/utils/billing-date';
import { ModalNames } from '~/types/modals';
import { getDialogRef } from '~/composables/dynamic-dialog';

interface BlockSeat {
    id: number;
    license_id: number;
    license_name: string | null;
    license_slug: string | null;
    person_id: number | null;
    person_name: string | null;
    person_email: string | null;
    auto_renew: boolean;
    expired: boolean;
    next_bill_date: string | null;
    price: number;
}

const dialogRef = getDialogRef(ModalNames.LICENSE_BLOCK_DRAWER);
const { showError } = useGlobalToast();

const data = computed(() => dialogRef.value?.data);


// A block can hold many seats; the list infinite-scrolls a page at a time.
const PER_PAGE = 20;

const seats = ref<BlockSeat[]>([]);
const isLoading = ref(true);
const loadingMore = ref(false);
const initialLoaded = ref(false);
// Every per-seat mutation still in flight. A Set (not a single id) because a
// second seat toggle can start before the first settles — the bulk switch must
// stay locked until ALL of them resolve, or an out-of-order settle can leave a
// seat opposite to the requested "renew all" state.
const togglingIds = ref(new Set<number>());
const bulkBusy = ref(false);
const searchQuery = ref('');
// The block's true seat count (from the last unfiltered load) — drives the
// empty state and the bulk-toggle guard independent of any active search.
const unfilteredTotal = ref(0);
const meta = ref<{
    current_page: number;
    last_page: number;
    per_page: number;
    total: number;
}>({ current_page: 1, last_page: 1, per_page: PER_PAGE, total: 0 });

const scrollRoot = ref<HTMLElement | null>(null);
const sentinel = ref<HTMLElement | null>(null);
let observer: IntersectionObserver | null = null;
let searchTimer: ReturnType<typeof setTimeout> | null = null;
// Monotonic guard: a search change or page jump can leave an older loadSeats()
// in flight. Only the newest request may mutate the visible list/meta.
let seatsLoadToken = 0;

const hasMore = computed(() => meta.value.current_page < meta.value.last_page);

// Block-level "renew all" reflects the most-pessimistic view: ON only when
// every loaded seat already has auto_renew on. Toggling it fires the bulk
// endpoint, which short-circuits seats already on the target value.
const bulkRenewing = computed(() => seats.value.length > 0 && seats.value.every((s) => s.auto_renew));

// The definitive block switch is trustworthy only when the whole block is
// visible: no active search filter, and every seat loaded. Otherwise an unseen
// seat could hold a different value, so we disable rather than mislead.
const bulkStateKnown = computed(() =>
    !searchQuery.value.trim() && meta.value.total > 0 && seats.value.length >= meta.value.total,
);

// Whether this block renews, from the block's own auto-renewing count — ONE state the
// header reads in every case.
//
// An earlier version of this read the loaded rows and gated on `bulkStateKnown`. That was
// wrong: any block over one page of licenses, and any block at all while a search is
// typed, has `bulkStateKnown` false, so the header printed "Renews <date>" for a block
// the row behind it marks Disabled — the very contradiction this drawer was changed to
// stop — and entering a search flipped it back to that date.
//
// The count comes from the row that opened the drawer, so it is there on the first paint,
// and it is passed live (see ReactiveModalData): the parent reloads its blocks through the
// `onChange` every successful toggle in here fires, and this follows that reload. `unref`,
// never `.value` — PrimeVue's reactive dialog instance unwraps the opener's ref on read
// while a hand-made `dialogRef` in a test does not.
//
// `=== 0`, so an absent count reads as unknown and the date stands rather than the drawer
// announcing a disabled renewal it was never told about.
const renewingCount = computed(() => unref(data.value?.autoRenewOn));
const renewalIsOff = computed(() => renewingCount.value === 0);

async function loadSeats(page = 1, { append = false } = {}) {
    if (!data.value) return;
    const token = ++seatsLoadToken;
    if (append) {
        loadingMore.value = true;
    } else {
        isLoading.value = true;
    }
    try {
        const params: Record<string, unknown> = {
            license_id: data.value.licenseId,
            page,
            per_page: PER_PAGE,
        };
        if (data.value.nextBillDate) params.next_bill_date = data.value.nextBillDate;
        const search = searchQuery.value.trim();
        if (search) params.search = search;

        // The seats endpoint returns a flat Laravel paginator (pagination fields
        // at the top level, rows under `data`), not the v3 `{ data, meta }`
        // envelope — read the fields off the body directly.
        const { data: body, status, message } = await usePbApi<any>(
            `/companies/${data.value.companyId}/financial/license-blocks/seats`,
            { params },
        );
        if (status < 200 || status >= 300) {
            throw new Error(message || 'Failed to load licenses');
        }
        // A newer load (different search/page) started while this awaited —
        // discard this response so it can't mix into or replace the current list.
        if (token !== seatsLoadToken) return;
        const rows: BlockSeat[] = body?.data ?? [];
        seats.value = append ? [...seats.value, ...rows] : rows;
        meta.value = {
            current_page: body?.current_page ?? page,
            last_page: body?.last_page ?? 1,
            per_page: body?.per_page ?? PER_PAGE,
            total: body?.total ?? seats.value.length,
        };
        if (!search) unfilteredTotal.value = meta.value.total;
    } catch (err: any) {
        if (token !== seatsLoadToken) return;
        showError('Error', err?.message ?? 'Failed to load licenses');
        if (!append) seats.value = [];
    } finally {
        // Only the newest request owns the loading flags; a superseded response
        // must not clear the indicator for the request that replaced it.
        if (token === seatsLoadToken) {
            isLoading.value = false;
            loadingMore.value = false;
            initialLoaded.value = true;
        }
    }
}

function loadMore() {
    if (isLoading.value || loadingMore.value || !hasMore.value) return;
    loadSeats(meta.value.current_page + 1, { append: true });
}

// Debounced search — reset to the first page and replace the list.
watch(searchQuery, () => {
    if (searchTimer) clearTimeout(searchTimer);
    searchTimer = setTimeout(() => loadSeats(1), 300);
});

// (Re)bind the infinite-scroll observer whenever the scroll container or the
// sentinel mounts/unmounts (the sentinel only renders while more pages exist).
watch([scrollRoot, sentinel], ([root, target]) => {
    observer?.disconnect();
    observer = null;
    if (root && target) {
        observer = new IntersectionObserver(
            (entries) => {
                if (entries.some((e) => e.isIntersecting)) loadMore();
            },
            { root, rootMargin: '160px' },
        );
        observer.observe(target);
    }
});

// Bulk-toggle every owned seat in this block. The drawer reloads the current
// page after the API resolves so counts + per-row state line up with reality.
async function handleBulkToggle(next: boolean) {
    if (!data.value || bulkBusy.value || togglingIds.value.size > 0) return;
    bulkBusy.value = true;
    try {
        await businessService.setBlockAutoRenew(
            data.value.companyId,
            data.value.licenseId,
            data.value.nextBillDate ?? null,
            next,
        );
        const onChange = dialogRef.value?.data?.onChange;
        if (typeof onChange === 'function') onChange();
        await loadSeats(1);
    } catch (err: any) {
        showError('Error', err?.message ?? 'Failed to update block auto-renew');
    } finally {
        bulkBusy.value = false;
    }
}

// Optimistic update: flip the row immediately, hit the API, roll back on
// failure. Same UX shape as the channel partner per-seat toggle.
async function handleToggle(seat: BlockSeat, next: boolean) {
    if (!data.value) return;
    if (seat.auto_renew === next) return;
    const previous = seat.auto_renew;
    seat.auto_renew = next;
    togglingIds.value.add(seat.id);
    try {
        await businessService.setLicenseAutoRenew(data.value.companyId, seat.id, next);
        // Tell the parent page so it can refresh the block summary card if the
        // change affects what it displays (e.g. renewal_cost on a fully off
        // block).
        const onChange = dialogRef.value?.data?.onChange;
        if (typeof onChange === 'function') onChange();
    } catch (err: any) {
        seat.auto_renew = previous;
        showError('Error', err?.message ?? 'Failed to update auto-renew');
    } finally {
        togglingIds.value.delete(seat.id);
    }
}

function closeDrawer() {
    dialogRef.value?.close();
}

onMounted(() => loadSeats(1));

onBeforeUnmount(() => {
    observer?.disconnect();
    if (searchTimer) clearTimeout(searchTimer);
});
</script>

<style scoped lang="scss">
.drawer-backdrop {
    @apply fixed inset-0 bg-black/40 z-50;
}

.block-drawer {
    @apply bg-white w-full sm:w-[520px] h-screen flex flex-col overflow-y-auto right-0 fixed top-0;
    box-shadow: -4px 0 24px rgba(0, 0, 0, 0.08);
}

.drawer-header {
    @apply flex justify-between items-start px-6 py-5 border-b border-zinc-200 flex-shrink-0;

    .header-content {
        @apply flex-1 min-w-0 pr-4;

        .block-name {
            @apply text-xl font-bold text-zinc-900 m-0 mb-1 truncate;
        }

        .block-id {
            @apply text-xs font-mono text-zinc-500 m-0;
        }

        .block-meta {
            @apply text-sm text-zinc-500 m-0 mt-1;
        }
    }

    .close-btn {
        @apply w-9 h-9 rounded-full flex items-center justify-center text-zinc-400 hover:text-zinc-600 hover:bg-zinc-100 transition-colors flex-shrink-0;
    }
}

.drawer-bulk {
    @apply flex items-center justify-between gap-4 px-6 py-4 border-b border-zinc-200 bg-zinc-50/60 flex-shrink-0;

    &__copy {
        @apply flex flex-col;
    }

    &__title {
        @apply text-sm font-medium text-zinc-900;
    }

    &__hint {
        @apply text-xs text-zinc-500 mt-0.5;
    }
}

.drawer-loading,
.drawer-empty {
    @apply p-6;
}

.drawer-empty {
    @apply text-sm text-zinc-500;
}

.drawer-body {
    @apply flex-1 overflow-y-auto px-6 py-6;
}

.drawer-section {
    .section-head {
        @apply flex items-baseline justify-between mb-3;

        .section-title {
            @apply text-xs font-semibold text-zinc-500 uppercase tracking-wider m-0;
        }

        .section-count {
            @apply text-xs text-zinc-400;
        }
    }
}

.seat-list {
    @apply m-0 p-0 list-none flex flex-col gap-2;
}

.seat-row {
    @apply flex items-center justify-between px-4 py-3 rounded-lg border border-zinc-200 bg-white;

    &--unassigned {
        @apply bg-zinc-50;
    }

    .seat-meta {
        @apply flex flex-col min-w-0;

        .seat-person {
            @apply text-sm font-medium text-zinc-900 truncate;
        }

        .seat-detail {
            @apply text-xs text-zinc-500 mt-0.5 truncate;
        }
    }

    .seat-toggle {
        @apply flex items-center gap-3 flex-shrink-0;

        .seat-toggle-label {
            @apply text-xs text-green-600 font-medium;

            &--off {
                @apply text-amber-600;
            }
        }
    }
}

.drawer-search {
    @apply relative mb-3;

    &__icon {
        @apply absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400 pointer-events-none;
    }

    &__input {
        @apply w-full h-9 pl-9 pr-3 rounded-lg border border-zinc-200 bg-white text-sm text-zinc-800;
        @apply focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-400;

        &::placeholder {
            @apply text-zinc-400;
        }
    }
}

.drawer-no-matches {
    @apply text-sm text-zinc-500 text-center py-6 m-0;
}

.drawer-sentinel {
    @apply mt-2;
    min-height: 1px;
}

.seat-list--loading {
    @apply opacity-60 pointer-events-none;
}

.drawer-help {
    @apply text-xs text-zinc-500 leading-relaxed mt-4 mb-0;
}

.slide-in-right-enter-active,
.slide-in-right-leave-active {
    transition: transform 0.25s ease;
}

.slide-in-right-enter-from,
.slide-in-right-leave-to {
    transform: translateX(100%);
}
</style>
